/* [AO kits] Sample kit scent picker behaviour (<sample-kit-picker>)
   Custom file — not part of Dawn 15.4.0.
   Built by: Claude (2026-10) + Codex (2026-10-27 kit filters)
   What it does: choose exactly N scents, search/filter, "About" pop-up; writes the Scents line property and blocks add to cart until complete.
   Used by: snippets/sample-kit-picker.liquid. */
if (!customElements.get('sample-kit-picker')) {
  customElements.define(
    'sample-kit-picker',
    class SampleKitPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.kitSize = Number(this.dataset.kitSize) || 0;
        this.noun = this.dataset.noun || 'scents';
        this.nounOne = this.dataset.nounOne || 'scent';
        this.formId = this.dataset.formId;
        this.storageKey = this.dataset.storageKey;
        this.includeAll = this.hasAttribute('data-include-all');
        this.soldOut = this.hasAttribute('data-sold-out');
        this.input = this.querySelector('[data-kit-input]');
        this.message = this.querySelector('[data-kit-message]');

        // Block every route into the cart while the kit is incomplete. Capturing on the
        // document runs before Dawn's product-form submit handler on the form itself.
        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        // About panel: read an item's description without leaving the kit (all modes).
        this.dialog = this.querySelector('[data-kit-dialog]');
        if (this.dialog) {
          this.dialog.addEventListener('close', () => this.onDialogClose());
        }
        this.addEventListener('click', this.onClick.bind(this));

        this.tiles = Array.from(this.querySelectorAll('[data-kit-tile]'));
        if (this.includeAll || this.soldOut) return;

        this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
        this.status = this.querySelector('[data-kit-status]');
        this.progress = this.querySelector('[data-kit-progress]');
        this.addButton = this.querySelector('[data-kit-add]');
        this.search = this.querySelector('[data-kit-search]');
        this.empty = this.querySelector('[data-kit-empty]');
        this.selected = this.restore();
        this.collectionFilter = this.querySelector('[data-kit-collection]');
        this.results = this.querySelector('[data-kit-results]');
        this.moreButton = this.querySelector('[data-kit-more]');
        this.view = 'all';
        this.pageSize = 12;
        this.visibleLimit = this.pageSize;

        if (this.search) this.search.addEventListener('input', () => this.filter(true));
        if (this.collectionFilter) this.collectionFilter.addEventListener('change', () => this.filter(true));
        this.querySelectorAll('[data-kit-browse], [data-kit-results-bar]').forEach((element) => (element.hidden = false));

        // The kit bar repeats the add action only while the page's own button is off screen.
        const submit = this.form && this.form.querySelector('[type="submit"]');
        if (submit && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.submitInView = entries[0].isIntersecting;
            this.updateAddButton();
          });
          this.observer.observe(submit);
        }

        // The bar is stuck while the end of the picker is below the viewport; only then can the
        // floating chat launcher sit over it, so only then does it reserve room for it.
        this.bar = this.querySelector('[data-kit-bar]');
        this.barEnd = document.createElement('span');
        this.barEnd.className = 'sample-kit__bar-end';
        this.bar.after(this.barEnd);
        if ('IntersectionObserver' in window) {
          // Stuck only while the picker has scrolled into view and its end is still below the fold;
          // before the picker reaches the screen the bar is not visible at all.
          this.barObserver = new IntersectionObserver(() => {
            const fold = window.innerHeight;
            const stuck = this.barEnd.getBoundingClientRect().top > fold && this.getBoundingClientRect().top < fold;
            this.bar.classList.toggle('is-stuck', stuck);
          });
          this.barObserver.observe(this.barEnd);
          this.barObserver.observe(this);
        }

        this.render();
      }

      disconnectedCallback() {
        if (this.barObserver) this.barObserver.disconnect();
        document.removeEventListener('submit', this.onSubmit, true);
        if (this.observer) this.observer.disconnect();
        this.initialized = false;
      }

      get form() {
        return document.getElementById(this.formId);
      }

      get remaining() {
        return Math.max(this.kitSize - this.selected.length, 0);
      }

      get complete() {
        if (this.soldOut) return false;
        return this.includeAll || this.selected.length === this.kitSize;
      }

      onSubmit(event) {
        if (event.target !== this.form) return;
        if (this.complete) {
          // The selection stays on screen if the add fails; only the stored draft is retired.
          this.forget();
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.soldOut) {
          this.showMessage(`This kit can't be ordered right now because not enough ${this.noun} are in stock to fill it.`);
        } else {
          this.showMessage(
            `Choose ${this.remaining} more ${this.remaining === 1 ? this.nounOne : this.noun} to complete your kit, then add it to your cart.`
          );
        }
        this.reveal();
      }

      onClick(event) {
        if (this.onDialogClick(event)) return;
        if (this.includeAll || this.soldOut) return;

        const clear = event.target.closest('[data-kit-search-clear]');
        if (clear) {
          this.search.value = '';
          this.filter(true);
          this.search.focus();
          return;
        }

        if (event.target.closest('[data-kit-reset]')) {
          if (this.search) this.search.value = '';
          if (this.collectionFilter) this.collectionFilter.value = '';
          this.view = 'all';
          this.filter(true);
          (this.search || this.querySelector('[data-kit-view="all"]')).focus();
          return;
        }

        const view = event.target.closest('[data-kit-view]');
        if (view) {
          this.view = view.dataset.kitView;
          // Review the whole chosen kit, even when a previous search/collection hid some of it.
          if (this.view === 'chosen') {
            if (this.search) this.search.value = '';
            if (this.collectionFilter) this.collectionFilter.value = '';
          }
          this.filter(true);
          return;
        }

        if (event.target.closest('[data-kit-more]')) {
          const previous = new Set(this.items.filter((item) => !item.hidden));
          this.visibleLimit += this.pageSize;
          this.filter();
          const next = this.items.find((item) => !item.hidden && !previous.has(item));
          if (next) next.querySelector('[data-kit-tile]').focus();
          return;
        }

        if (event.target.closest('[data-kit-add]')) {
          const submit = this.form && this.form.querySelector('[type="submit"]');
          if (submit) submit.click();
          return;
        }

        const tile = event.target.closest('[data-kit-tile]');
        if (tile) this.toggle(tile.dataset.id);
      }

      // Returns 'added', 'removed', 'full' or null when the item cannot be chosen.
      toggle(id) {
        const tile = this.tiles.find((item) => item.dataset.id === id);
        if (!tile || tile.disabled) return null;
        const at = this.selected.indexOf(id);
        if (at > -1) {
          this.selected.splice(at, 1);
        } else if (this.selected.length >= this.kitSize) {
          this.render();
          this.showMessage(`Your kit is full. Tap a chosen ${this.nounOne} to remove it, then choose another.`, 'info');
          return 'full';
        } else {
          this.selected.push(id);
        }
        this.render();
        this.save();
        return at > -1 ? 'removed' : 'added';
      }

      onDialogClick(event) {
        const opener = event.target.closest('[data-kit-about]');
        if (opener) {
          this.openAbout(opener);
          return true;
        }
        if (!this.dialog || !this.dialog.open) return false;
        // A click on the dialog box itself, outside the panel, is a click on the backdrop.
        if (event.target === this.dialog || event.target.closest('[data-kit-dialog-close]')) {
          this.dialog.close();
          return true;
        }
        if (event.target.closest('[data-kit-dialog-toggle]')) {
          const result = this.toggle(this.aboutId);
          if (result === 'added' || result === 'removed') this.dialog.close();
          else this.updateAbout();
          return true;
        }
        return event.target.closest('[data-kit-dialog]') !== null;
      }

      openAbout(opener) {
        if (!this.dialog) return;
        this.aboutId = opener.dataset.kitAbout;
        this.aboutOpener = opener;
        const content = this.querySelector(`template[data-kit-about-content="${this.aboutId}"]`);
        this.dialog.querySelector('[data-kit-dialog-title]').textContent = opener.dataset.name;
        const body = this.dialog.querySelector('[data-kit-dialog-body]');
        body.replaceChildren(content ? content.content.cloneNode(true) : document.createTextNode(''));
        body.scrollTop = 0;
        this.dialog.querySelector('[data-kit-dialog-link]').href = opener.dataset.url;
        this.updateAbout();
        document.documentElement.classList.add('sample-kit-locked');
        this.dialog.showModal();
        this.dialog.querySelector('[data-kit-dialog-close]').focus();
      }

      // The panel's action mirrors the tile: add, remove, or explain why it cannot be added.
      updateAbout() {
        const button = this.dialog.querySelector('[data-kit-dialog-toggle]');
        const note = this.dialog.querySelector('[data-kit-dialog-note]');
        const tile = this.tiles.find((item) => item.dataset.id === this.aboutId);
        let label = '';
        let text = '';
        if (this.includeAll) {
          text = `Included in this kit.`;
        } else if (!tile || tile.disabled) {
          text = `Sold out right now, so it can't be added to your kit.`;
        } else if (this.selected.includes(this.aboutId)) {
          label = 'Remove from my kit';
          text = `In your kit · ${this.selected.length} of ${this.kitSize} chosen.`;
        } else if (this.selected.length >= this.kitSize) {
          text = `Your kit is full. Remove a chosen ${this.nounOne} first to add this one.`;
        } else {
          label = 'Add to my kit';
          text = `${this.selected.length} of ${this.kitSize} chosen.`;
        }
        button.hidden = !label;
        button.textContent = label;
        button.classList.toggle('button--secondary', label === 'Remove from my kit');
        button.classList.toggle('button--primary', label !== 'Remove from my kit');
        note.hidden = !text;
        note.textContent = text;
      }

      onDialogClose() {
        document.documentElement.classList.remove('sample-kit-locked');
        if (this.aboutOpener && document.contains(this.aboutOpener)) {
          const item = this.aboutOpener.closest('[data-kit-item]');
          if (item && item.hidden) this.querySelector('[data-kit-view="chosen"]').focus();
          else this.aboutOpener.focus({ preventScroll: true });
        }
      }

      render() {
        const count = this.selected.length;
        const full = count >= this.kitSize;

        this.tiles.forEach((tile) => {
          const isSelected = this.selected.includes(tile.dataset.id);
          tile.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          tile.classList.toggle('is-selected', isSelected);
          tile.classList.toggle('is-locked', full && !isSelected && !tile.disabled);
        });

        const names = this.selected
          .map((id) => this.tiles.find((tile) => tile.dataset.id === id))
          .filter(Boolean)
          .map((tile) => tile.dataset.name);
        this.input.value = this.complete ? names.join(', ') : '';

        this.status.innerHTML = `<strong>${count} of ${this.kitSize} chosen</strong> · ${
          this.complete ? 'your kit is complete' : `choose ${this.remaining} more`
        }`;
        this.status.classList.toggle('is-complete', this.complete);
        this.progress.style.width = `${(count / this.kitSize) * 100}%`;
        this.updateAddButton();
        this.hideMessage();
        this.filter();
      }

      updateAddButton() {
        if (this.addButton) this.addButton.hidden = !this.complete || Boolean(this.submitInView);
      }

      filter(reset = false) {
        if (reset) this.visibleLimit = this.pageSize;
        const query = this.normalize(this.search ? this.search.value : '');
        const collection = this.collectionFilter ? this.collectionFilter.value : '';
        const matches = this.items.filter((item) => {
          const tile = item.querySelector('[data-kit-tile]');
          return (!query || this.normalize(item.dataset.search).includes(query)) &&
            (!collection || (item.dataset.collections || '').split('|').includes(collection)) &&
            (this.view !== 'chosen' || this.selected.includes(tile.dataset.id));
        });
        const visible = new Set(matches.slice(0, this.visibleLimit));
        const focusedItem = document.activeElement && document.activeElement.closest('[data-kit-item]');
        this.items.forEach((item) => (item.hidden = !visible.has(item)));
        if (focusedItem && this.contains(focusedItem) && focusedItem.hidden && !this.dialog?.open) {
          this.querySelector('[data-kit-view="chosen"]').focus();
        }
        if (this.results) this.results.textContent = `Showing ${visible.size} of ${matches.length} ${this.noun}${this.view === 'chosen' ? ' in your kit' : ''}`;
        if (this.moreButton) {
          this.moreButton.hidden = visible.size >= matches.length;
          this.moreButton.textContent = `Show ${Math.min(this.pageSize, matches.length - visible.size)} more ${this.noun}`;
        }
        if (this.empty) {
          this.empty.hidden = matches.length > 0;
          this.empty.querySelector('[data-kit-empty-message]').textContent = this.view === 'chosen' && this.selected.length === 0
            ? `You haven't chosen any ${this.noun} yet. Browse all ${this.noun} to start your kit.`
            : `No ${this.noun} match these filters. Try another search or clear the filters.`;
        }
        this.querySelectorAll('[data-kit-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.kitView === this.view)));
        this.querySelectorAll('[data-kit-chosen-count]').forEach((count) => (count.textContent = this.selected.length));
        const resetButton = this.querySelector('[data-kit-results-bar] [data-kit-reset]');
        if (resetButton) resetButton.hidden = !query && !collection && this.view === 'all';
        this.querySelectorAll('.sample-kit__search-clear').forEach((button) => (button.hidden = !query));
      }

      normalize(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      // Bring the count and its message into view; the message itself is announced as an alert.
      reveal() {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const target = this.message && !this.message.hidden ? this.message : this;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }

      showMessage(text, tone = 'error') {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.classList.toggle('is-info', tone === 'info');
        // A hint about a full kit should not linger beside a valid, complete kit.
        clearTimeout(this.messageTimer);
        if (tone === 'info') this.messageTimer = setTimeout(() => this.hideMessage(), 5000);
        this.message.setAttribute('role', tone === 'info' ? 'status' : 'alert');
        this.message.hidden = false;
      }

      hideMessage() {
        if (!this.message) return;
        this.message.hidden = true;
        this.message.removeAttribute('role');
      }

      restore() {
        try {
          const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
          const valid = this.tiles.filter((tile) => !tile.disabled).map((tile) => tile.dataset.id);
          return stored.filter((id, i) => valid.includes(id) && stored.indexOf(id) === i).slice(0, this.kitSize);
        } catch (e) {
          return [];
        }
      }

      save() {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.selected));
        } catch (e) {}
      }

      forget() {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (e) {}
      }
    }
  );
}
